(() => {
  if (customElements.get('bm-routine-builder')) return;
  const escapeHTML = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[character]));
  const normalize = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const decodeText = (value) => { const decoder = document.createElement('textarea'); decoder.innerHTML = String(value ?? ''); return decoder.value; };
  const ingredientNames = {magneziu:'magneziu',calciu:'calciu',zinc:'zinc',seleniu:'seleniu',melatonina:'melatonină',fier:'fier',folat:'folat (vitamina B9)',crom:'crom',cupru:'cupru',mangan:'mangan',molibden:'molibden',potasiu:'potasiu',ala:'acid alfa-lipoic',coq10:'coenzima Q10',nac:'N-acetilcisteină'};
  const canonicalToken = (value) => {
    const token = normalize(value).replace(/[_\s]+/g, '-');
    const vitamin = token.match(/^(?:vit|vitamina|vitamin)-?(b(?:1[02]|[1-9])|c|d3?|e|a|k[12]?)$/);
    if (vitamin) return vitamin[1] === 'b9' ? 'folat' : `vit-${vitamin[1] === 'd3' ? 'd' : /^k/.test(vitamin[1]) ? 'k' : vitamin[1]}`;
    return ({mg:'magneziu',ca:'calciu',zn:'zinc',se:'seleniu',fe:'fier',cr:'crom',cu:'cupru',mn:'mangan',mo:'molibden',k:'potasiu',magnesium:'magneziu',calcium:'calciu',selenium:'seleniu',melatonin:'melatonina','acid-folic':'folat'})[token] || token;
  };
  const tokenLabel = (token) => ingredientNames[token] || (/^vit-/.test(token) ? `vitamina ${token.slice(4).toUpperCase()}` : token.replace(/[-_]+/g, ' '));
  class BiomeRoutineBuilder extends HTMLElement {
    static quantities(items) {
      const quantities = new Map();
      for (const item of items || []) {
        const variant = String(item.variant_id ?? item.variant ?? item.id ?? '');
        const quantity = Math.max(0, Math.trunc(Number(item.quantity)) || 0);
        if (variant) quantities.set(variant, (quantities.get(variant) || 0) + quantity);
      }
      return quantities;
    }
    static remainingQuantity(product, quantityInCart = 0) {
      if (!product.available) return 0;
      const limits = [product.max];
      for (const value of [product.quantityMaximum, product.stock]) {
        if (value !== null && value !== undefined && Number.isFinite(Number(value))) limits.push(Math.max(0, Math.trunc(Number(value)) - quantityInCart));
      }
      return Math.max(0, Math.min(...limits));
    }
    static estimate(product, quantity) {
      const rate = product.discountEligible ? quantity >= 6 ? 10 : quantity >= 3 ? 5 : 0 : 0;
      const unit = Math.round(product.price * (100 - rate) / 100);
      return {rate, unit, total:unit * quantity, standard:product.price * quantity, daily:product.days > 0 ? unit / product.days : null};
    }
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.storageKey = 'bm-shopping-routine-v1';
      this.pendingKey = 'bm-shopping-routine-pending-v1';
      this.selection = new Map();
      this.slot = 'morning';
      this.filter = 'all';
      this.query = '';
      this.busy = false;
      this.initializing = true;
      this.reconcileRequired = false;
      this.currency = this.dataset.currency || 'RON';
      try {
        const data = JSON.parse(this.querySelector('[data-routine-catalog]').textContent);
        this.products = (data.products || []).filter((product) => product.variant && Number.isFinite(product.price) && product.price >= 0).map((product) => ({
          ...product,
          variant: String(product.variant),
          title: decodeText(product.title),
          detail: decodeText(product.detail),
          days: Math.max(0, Number(product.days) || 0),
          max: Math.max(0, Math.min(Math.trunc(Number(product.max)) || 9, 9)),
          discountEligible: product.discountEligible === true,
          tokens: Array.isArray(product.tokens) ? [...new Set(product.tokens.map(canonicalToken).filter(Boolean))] : [],
          group: normalize(product.type) === 'vitamine' ? 'vitamins' : normalize(product.type) === 'minerale' ? 'minerals' : 'other'
        }));
        this.productsByVariant = new Map(this.products.map((product) => [product.variant, product]));
        this.cartQuantities = BiomeRoutineBuilder.quantities(data.cart);
      } catch {
        const message = document.createElement('p');
        message.textContent = 'Nu am putut încărca produsele pentru rutină. Te rugăm să folosești catalogul.';
        this.append(message);
        return;
      }
      this.restoreSelection();
      try { this.reconcileRequired = Boolean(sessionStorage.getItem(this.pendingKey)); } catch {}
      if (this.reconcileRequired) this.feedback('O adăugare anterioară nu a fost confirmată. Verifică produsele în coș înainte de a începe o listă nouă.');
      this.querySelector('[data-routine-workspace]').hidden = false;
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.querySelector('[data-routine-search]').addEventListener('input', (event) => {
        this.query = normalize(event.target.value);
        this.renderCatalog();
      });
      this.update(false);
      this.initialCartRead = this.refreshCart(true).catch(() => {
        this.feedback('Cantitățile disponibile se vor verifica din nou înainte de adăugarea în coș.');
      }).finally(() => { this.initializing = false; this.update(false); });
    }
    money(cents) {
      const amount = Number(cents) / 100;
      if (this.currency === 'RON') return `${new Intl.NumberFormat('ro-RO', {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(amount)} lei`;
      return new Intl.NumberFormat('ro-RO', {style: 'currency', currency: this.currency}).format(amount);
    }
    remaining(product) {
      return BiomeRoutineBuilder.remainingQuantity(product, this.cartQuantities.get(product.variant) || 0);
    }
    feedback(message, focus = false) {
      const box = this.querySelector('[data-routine-feedback]');
      box.textContent = message;
      if (focus) box.focus({preventScroll:true});
    }
    restoreSelection() {
      let rows = [];
      try { rows = JSON.parse(sessionStorage.getItem(this.storageKey) || '[]'); } catch { rows = []; }
      const requested = new URLSearchParams(location.hash.slice(1)).get('produse');
      if (requested) rows = requested.split(',').map((variant) => ({variant, slot:'morning', quantity:1}));
      if (!Array.isArray(rows)) return;
      let skipped = false;
      rows.forEach((row) => {
        const product = this.productsByVariant.get(String(row.variant));
        const remaining = product ? this.remaining(product) : 0;
        if (!product || remaining < 1 || this.selection.has(product.variant)) { skipped = true; return; }
        this.selection.set(product.variant, {variant:product.variant, slot:row.slot === 'evening' ? 'evening' : 'morning', quantity:Math.max(1, Math.min(Math.trunc(Number(row.quantity)) || 1, remaining))});
      });
      if (skipped) this.feedback('Am păstrat produsele disponibile din lista ta. Verifică rutina înainte de a o adăuga în coș.');
    }
    saveSelection() {
      try { sessionStorage.setItem(this.storageKey, JSON.stringify([...this.selection.values()])); } catch {}
    }
    clearPending() {
      try { sessionStorage.removeItem(this.pendingKey); } catch {}
    }
    onClick(event) {
      if (event.target.closest('[data-routine-reset]') && !this.busy) {
        this.selection.clear();
        this.reconcileRequired = false;
        this.clearPending();
        this.update();
        this.feedback('Lista a fost golită. Produsele din coș sunt păstrate.', true);
        return;
      }
      const filter = event.target.closest('[data-routine-filter]');
      if (filter) {
        this.filter = filter.dataset.routineFilter;
        this.querySelectorAll('[data-routine-filter]').forEach((button) => button.setAttribute('aria-pressed', String(button === filter)));
        this.renderCatalog();
        return;
      }
      const add = event.target.closest('[data-routine-add]');
      if (add && !this.busy) {
        const product = this.productsByVariant.get(add.dataset.routineAdd);
        if (!product || this.remaining(product) < 1 || this.selection.has(product.variant)) return;
        this.selection.set(product.variant, {variant:product.variant, slot:this.slot, quantity:1});
        this.feedback(`${product.title} a fost adăugat în rutină.`);
        this.update();
        this.querySelector(`#bm-routine-${this.slot}`).focus({preventScroll:true});
        return;
      }
      const remove = event.target.closest('[data-routine-remove]');
      if (remove && !this.busy) {
        const row = this.selection.get(remove.dataset.routineRemove);
        if (!row) return;
        this.selection.delete(row.variant);
        this.feedback('Produsul a fost eliminat din rutină.');
        this.update();
        this.querySelector(`#bm-routine-${row.slot}`).focus({preventScroll:true});
        return;
      }
      if (event.target.closest('[data-routine-add-all]')) this.addAll();
    }
    onChange(event) {
      if (event.target.name === 'routine-slot') {
        this.slot = event.target.value === 'evening' ? 'evening' : 'morning';
        this.renderCatalog();
        return;
      }
      const quantity = event.target.closest('[data-routine-quantity]');
      if (quantity && !this.busy) {
        const row = this.selection.get(quantity.dataset.routineQuantity);
        const product = this.productsByVariant.get(quantity.dataset.routineQuantity);
        if (!row || !product) return;
        const remaining = this.remaining(product);
        if (remaining < 1) { this.selection.delete(row.variant); this.update(); return; }
        row.quantity = Math.max(1, Math.min(Math.trunc(Number(quantity.value)) || 1, remaining));
        quantity.value = row.quantity;
        this.update();
        this.querySelector(`[data-routine-quantity="${product.variant}"]`)?.focus({preventScroll:true});
        return;
      }
      const period = event.target.closest('[data-routine-period]');
      if (period && !this.busy) {
        const row = this.selection.get(period.dataset.routinePeriod);
        if (!row) return;
        row.slot = period.value === 'evening' ? 'evening' : 'morning';
        this.update();
        this.querySelector(`[data-routine-period="${row.variant}"]`)?.focus({preventScroll:true});
      }
    }
    update(save = true) {
      if (save) this.saveSelection();
      this.renderCatalog();
      this.renderSelection();
    }
    renderCatalog() {
      const products = this.products.filter((product) => {
        const text = normalize(`${product.title} ${product.detail} ${product.tokens.join(' ')}`);
        return (this.filter === 'all' || product.group === this.filter) && (!this.query || text.includes(this.query));
      });
      this.querySelector('[data-routine-count]').textContent = `${products.length} ${products.length === 1 ? 'produs' : 'produse'} ${this.query || this.filter !== 'all' ? 'pentru această selecție' : 'în catalog'}`;
      this.querySelector('[data-routine-no-results]').hidden = products.length > 0;
      this.querySelector('[data-routine-products]').innerHTML = products.map((product) => {
        const selected = this.selection.has(product.variant);
        const remaining = this.remaining(product);
        const label = remaining < 1 ? product.available && this.cartQuantities.get(product.variant) ? 'Cantitate deja în coș' : 'Indisponibil' : selected ? 'Deja în rutină' : `Adaugă ${this.slot === 'morning' ? 'dimineața' : 'seara'}`;
        return `<article class="bm-routine__product"><a href="${escapeHTML(product.url)}" class="bm-routine__product-photo" tabindex="-1" aria-hidden="true">${product.image ? `<img src="${escapeHTML(product.image)}" width="240" height="240" alt="" loading="lazy" decoding="async">` : ''}</a><div class="bm-routine__product-info"><h3><a href="${escapeHTML(product.url)}">${escapeHTML(product.title)}</a></h3><p class="bm-routine__product-detail">${escapeHTML(product.detail)}</p><div class="bm-routine__product-prices"><strong>${escapeHTML(this.money(product.price))}</strong>${product.days > 0 ? `<small>${escapeHTML(this.money(product.price / product.days))}/zi</small>` : ''}</div></div><button type="button" class="bm-routine__product-add" data-routine-add="${product.variant}" ${remaining < 1 || selected || this.busy ? 'disabled' : ''} aria-label="${escapeHTML(`${label}: ${product.title}`)}">${label}</button></article>`;
      }).join('');
    }
    renderSelection() {
      const rows = [...this.selection.values()];
      let total = 0;
      let standard = 0;
      let daily = 0;
      let allKnown = rows.length > 0;
      for (const row of rows) {
        const product = this.productsByVariant.get(row.variant);
        const estimate = BiomeRoutineBuilder.estimate(product, row.quantity);
        total += estimate.total;
        standard += estimate.standard;
        if (estimate.daily === null) allKnown = false;
        else daily += estimate.daily;
      }
      ['morning','evening'].forEach((slot) => {
        const slotRows = rows.filter((row) => row.slot === slot);
        this.querySelector(`[data-routine-empty-${slot}]`).hidden = slotRows.length > 0;
        this.querySelector(`[data-routine-${slot}]`).innerHTML = slotRows.map((row) => {
          const product = this.productsByVariant.get(row.variant);
          const estimate = BiomeRoutineBuilder.estimate(product, row.quantity);
          const remaining = this.remaining(product);
          const inCart = this.cartQuantities.get(row.variant) || 0;
          return `<li class="bm-routine__item">${product.image ? `<img src="${escapeHTML(product.image)}" alt="" width="42" height="58" loading="lazy">` : '<span></span>'}<div><a class="bm-routine__item-title" href="${escapeHTML(product.url)}">${escapeHTML(product.title)}</a><p class="bm-routine__item-price">${estimate.rate ? `<s>${escapeHTML(this.money(estimate.standard))}</s> ` : ''}${escapeHTML(this.money(estimate.total))}${estimate.rate ? ` <span class="bm-routine__item-discount">−${estimate.rate}%</span>` : ''}</p>${product.days > 0 ? `<p class="bm-routine__item-duration">${row.quantity * product.days} zile, la utilizarea de pe etichetă</p>` : ''}${inCart ? `<p class="bm-routine__item-duration">Deja în coș: ${inCart}. Cantitatea de mai jos se adaugă.</p>` : ''}</div><div class="bm-routine__item-controls"><label for="routine-qty-${row.variant}">Cantitate<input type="number" id="routine-qty-${row.variant}" min="1" max="${remaining}" value="${row.quantity}" step="1" inputmode="numeric" data-routine-quantity="${row.variant}" aria-label="Cantitate pentru ${escapeHTML(product.title)}" ${this.busy || remaining < 1 ? 'disabled' : ''}></label><label for="routine-period-${row.variant}">Moment ales<select id="routine-period-${row.variant}" data-routine-period="${row.variant}" aria-label="Moment ales pentru ${escapeHTML(product.title)}" ${this.busy ? 'disabled' : ''}><option value="morning" ${slot === 'morning' ? 'selected' : ''}>Dimineața</option><option value="evening" ${slot === 'evening' ? 'selected' : ''}>Seara</option></select></label><button type="button" class="bm-routine__item-remove" data-routine-remove="${row.variant}" aria-label="Elimină ${escapeHTML(product.title)} din rutină" ${this.busy ? 'disabled' : ''}>×</button></div></li>`;
        }).join('');
      });
      this.querySelector('[data-routine-total]').textContent = this.money(total);
      this.querySelector('[data-routine-standard-total]').textContent = this.money(standard);
      this.querySelector('[data-routine-standard-row]').hidden = standard === total;
      this.querySelector('[data-routine-saving]').textContent = this.money(standard - total);
      this.querySelector('[data-routine-saving-row]').hidden = standard === total;
      this.querySelector('[data-routine-summary]').textContent = `${rows.length} ${rows.length === 1 ? 'produs' : 'produse'}`;
      this.querySelector('[data-routine-summary-total]').textContent = this.money(total);
      this.querySelector('[data-routine-daily-row]').hidden = !allKnown;
      this.querySelector('[data-routine-daily]').textContent = this.money(daily);
      this.querySelector('[data-routine-daily-note]').textContent = !rows.length ? 'Alege cel puțin un produs pentru a vedea costul.' : allKnown ? 'Media folosește prețul estimat al fiecărui ambalaj și durata verificată de pe etichetă. Cantitatea suplimentară prelungește durata.' : 'Costul total pe zi nu este afișat deoarece durata de utilizare nu este verificată pentru toate produsele alese. Costurile individuale cunoscute apar în catalog.';
      this.renderWarnings(rows);
      const button = this.querySelector('[data-routine-add-all]');
      button.disabled = rows.length === 0 || this.busy || this.initializing || this.reconcileRequired;
      button.textContent = this.busy ? 'Se verifică și se adaugă…' : this.initializing ? 'Se verifică disponibilitatea…' : this.reconcileRequired ? 'Verifică produsele în coș' : 'Adaugă rutina în coș';
      this.querySelector('[data-routine-workspace]').setAttribute('aria-busy', String(this.busy));
      this.querySelector('[data-routine-reset]').hidden = !this.reconcileRequired;
    }
    renderWarnings(rows) {
      const tokens = new Map();
      let hasUnknown = false;
      rows.forEach((row) => {
        const product = this.productsByVariant.get(row.variant);
        if (!product.tokens.length) hasUnknown = true;
        product.tokens.forEach((token) => {
          if (!tokens.has(token)) tokens.set(token, []);
          tokens.get(token).push(product.title);
        });
      });
      const duplicates = [...tokens.entries()].filter(([, titles]) => titles.length > 1);
      const messages = [];
      if (duplicates.length) {
        const labels = duplicates.map(([token]) => tokenLabel(token)).join(', ');
        messages.push(`Ingredient${duplicates.length === 1 ? ' comun' : 'e comune'}: ${labels}. Compară dozele de pe etichete înainte de a folosi produsele împreună.`);
      }
      if (rows.length > 1 && hasUnknown) messages.push('Pentru unele produse nu avem ingrediente structurate. Verifică toate formulele înainte de utilizare; absența unei atenționări nu confirmă compatibilitatea.');
      const box = this.querySelector('[data-routine-warnings]');
      box.hidden = messages.length === 0;
      box.innerHTML = messages.map((message) => `<p>${escapeHTML(message)}</p>`).join('');
    }
    async readCart() {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(this.dataset.cartRead, {credentials:'same-origin', cache:'no-store', headers:{Accept:'application/json'}, signal:controller.signal});
        if (!response.ok) throw new Error('Nu am putut verifica produsele deja în coș.');
        const cart = await response.json();
        if (!Array.isArray(cart.items)) throw new Error('Răspunsul coșului nu a putut fi verificat.');
        if (cart.currency && cart.currency !== this.currency) throw new Error('Moneda coșului s-a schimbat. Reîncarcă pagina pentru prețurile actuale.');
        return cart;
      } finally { clearTimeout(timeout); }
    }
    adjustSelection() {
      let changed = false;
      for (const row of this.selection.values()) {
        const remaining = this.remaining(this.productsByVariant.get(row.variant));
        if (remaining < 1) { this.selection.delete(row.variant); changed = true; }
        else if (row.quantity > remaining) { row.quantity = remaining; changed = true; }
      }
      if (changed) this.saveSelection();
      return changed;
    }
    async refreshCart(adjust = false) {
      const cart = await this.readCart();
      this.cartQuantities = BiomeRoutineBuilder.quantities(cart.items);
      if (adjust && this.adjustSelection()) this.feedback('Am actualizat rutina pentru cantitățile disponibile, ținând cont de produsele deja în coș.');
      this.update(false);
      return cart;
    }
    openCart() { location.assign(this.dataset.cartUrl); }
    async addAll() {
      if (this.busy || this.initializing || this.reconcileRequired || !this.selection.size) return;
      this.busy = true;
      this.feedback('Verificăm cantitățile disponibile…');
      this.update(false);
      let before;
      let sent = false;
      let response;
      try {
        await this.refreshCart(false);
        before = new Map(this.cartQuantities);
        if (this.adjustSelection()) {
          this.busy = false;
          this.update();
          this.feedback('Cantitatea disponibilă s-a schimbat. Am actualizat lista; verifică prețul și apasă din nou pentru a adăuga rutina.', true);
          return;
        }
        const items = [...this.selection.values()].map((row) => ({id:row.variant, quantity:row.quantity}));
        if (!items.length) { this.busy = false; this.update(false); return; }
        this.feedback('Adăugăm rutina în coș…');
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 20000);
        let result;
        try {
          sent = true;
          try { sessionStorage.setItem(this.pendingKey, JSON.stringify(items)); } catch {}
          response = await fetch(this.dataset.cartAdd, {method:'POST', credentials:'same-origin', headers:{'Content-Type':'application/json', Accept:'application/json'}, body:JSON.stringify({items}), signal:controller.signal});
          result = await response.json();
        } finally { clearTimeout(timeout); }
        if (!response.ok) throw new Error(result.description || result.message || 'Unele produse nu mai sunt disponibile.');
        if (!Array.isArray(result.items)) throw new Error('Adăugarea nu a putut fi confirmată.');
        const added = BiomeRoutineBuilder.quantities(result.items);
        if (items.some((item) => (added.get(item.id) || 0) < item.quantity)) throw new Error('Nu am putut confirma toate produsele din rutină.');
        try { sessionStorage.removeItem(this.storageKey); } catch {}
        this.clearPending();
        this.feedback('Rutina a fost adăugată. Deschidem coșul…');
        this.openCart();
      } catch (error) {
        const message = error instanceof Error && error.name !== 'AbortError' ? error.message : 'Conexiunea nu a putut fi confirmată.';
        if (sent) {
          // A failed multi-item request can still change stock quantities. Never repeat it automatically.
          this.reconcileRequired = true;
          try {
            await this.refreshCart(false);
            const changed = [...this.selection.keys()].some((variant) => (this.cartQuantities.get(variant) || 0) !== (before.get(variant) || 0));
            // An explicit Shopify validation rejection with an unchanged cart can be corrected safely.
            if (response?.status === 422 && !changed) { this.reconcileRequired = false; this.clearPending(); this.adjustSelection(); }
          } catch {}
        }
        this.busy = false;
        this.update();
        this.feedback(this.reconcileRequired ? `${message} Deschide coșul pentru a verifica produsele; adăugarea repetată este oprită ca să evităm dublarea.` : `${message} Verifică lista și reîncearcă atunci când conexiunea este disponibilă.`, true);
      }
    }
  }
  customElements.define('bm-routine-builder', BiomeRoutineBuilder);
})();
